// src/modules/cv/SegmentationLab.jsx
// Segmentation Lab — K-Means color segmentation, Watershed concept, concepts guide

import React, { useState, useCallback, useEffect } from 'react';
import { generateSynth, imgToImageData, imageDataToURL, kmeansSegment } from '../../utils/cvUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const TABS = ['upload', 'kmeans', 'watershed', 'concepts'];
const Btn = ({onClick,children,color='var(--green)',disabled})=>(<button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1}}>{children}</button>);

const SYNTH = [{name:'Shapes',type:'shapes'},{name:'Circles',type:'circles'},{name:'Gradient',type:'gradient'},{name:'Portrait',type:'portrait'}];

const CONCEPTS = [
  {title:'K-Means Color Segmentation',color:'var(--green)',desc:'Groups pixels into K clusters by minimizing within-cluster RGB distance. Lloyd\'s algorithm: assign each pixel to nearest centroid, then update centroids as cluster means. Converges in ~10–20 iterations.'},
  {title:'Watershed Transform',color:'var(--cyan)',desc:'Treats grayscale image as topographic surface. "Floods" from local minima (markers). Watershed lines form at flood meeting points. Excellent for separating touching objects like cells or coins.'},
  {title:'GrabCut',color:'var(--gold)',desc:'Interactive segmentation using Gaussian Mixture Models (GMM) for foreground/background. Uses graph cuts for energy minimization. User defines a bounding box, algorithm iterates to refine the mask. Used in Adobe tools.'},
  {title:'Semantic vs Instance',color:'var(--violet)',desc:'Semantic segmentation labels every pixel with a class (sky, road, car). Instance segmentation also distinguishes between individual objects of the same class. Instance is harder — requires detecting AND separating each object.'},
  {title:'Deep Segmentation',color:'var(--orange)',desc:'U-Net (encoder-decoder with skip connections), FCN (Fully Convolutional Networks), DeepLab (atrous convolution + CRF). Segment Anything Model (SAM) from Meta is a foundation model that zero-shot segments any object.'},
  {title:'Evaluation Metrics',color:'var(--red)',desc:'IoU (Intersection over Union) = |A∩B| / |A∪B|. Dice coefficient = 2|A∩B|/(|A|+|B|). mAP (mean Average Precision) for instance segmentation. Pixel accuracy is misleading on imbalanced datasets.'},
];

export default function SegmentationLab({ seedImageSrc = null, hideUpload = false }) {
  const [tab, setTab] = useState(hideUpload ? 'kmeans' : 'upload');
  const [imgSrc, setImgSrc] = useState(null);
  const [imgData, setImgData] = useState(null);
  const [k, setK] = useState(4);
  const [segResult, setSegResult] = useState(null);
  const [processing, setProcessing] = useState(false);

  const loadSrc = useCallback(async (src) => {
    setImgSrc(src); const id = await imgToImageData(src); setImgData(id);
    setSegResult(null);
    if (tab==='upload') setTab('kmeans');
  }, [tab]);

  useEffect(() => {
    if (seedImageSrc) loadSrc(seedImageSrc);
  }, [seedImageSrc, loadSrc]);

  useEffect(() => {
    if (hideUpload && tab === 'upload') setTab('kmeans');
  }, [hideUpload, tab]);

  const handleFile = (file) => {
    if (!file?.type.startsWith('image/')) return;
    const r = new FileReader(); r.onload = e => loadSrc(e.target.result); r.readAsDataURL(file);
  };

  const runKMeans = async () => {
    if (!imgData) return; setProcessing(true);
    await new Promise(r => setTimeout(r, 10));
    const result = kmeansSegment(imgData, k, 12);
    setSegResult({...result, segSrc: imageDataToURL(result.imageData)});
    setProcessing(false);
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {(hideUpload ? TABS.filter(t => t !== 'upload') : TABS).map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--green)':'transparent'}`,background:tab===t?'var(--ggreen)':'transparent',color:tab===t?'var(--green)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {tab==='upload' && !hideUpload && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}} onDragOver={e=>e.preventDefault()} onClick={()=>document.getElementById('seg-file').click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input id="seg-file" type="file" accept="image/*" style={{display:'none'}} onChange={e=>handleFile(e.target.files[0])}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>⬡</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop Image</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Color images with distinct regions segment best</div>
          </div>
          <Panel title="SYNTHETIC TEST IMAGES" accent="var(--green)">
            {SYNTH.map(({name,type})=>(
              <div key={type} onClick={()=>loadSrc(generateSynth(type,256))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 12px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}} onMouseEnter={e=>e.currentTarget.style.borderColor='var(--green)'} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b0)'}>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div></div>
                <span style={{color:'var(--green)'}}>→</span>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {tab==='kmeans' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="K-MEANS COLOR SEGMENTATION" accent="var(--green)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'12px'}}>
              Clusters all pixels into K color groups using Lloyd's algorithm. Each output pixel gets the color of its cluster centroid. Lower K = more abstract, Higher K = more detail preserved.
            </div>
            <div style={{marginBottom:'12px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:'5px'}}>
                <span style={{fontSize:'10px',color:'var(--t3)'}}>Number of Clusters (K)</span>
                <span style={{fontSize:'10px',color:'var(--green)',fontWeight:700}}>{k} clusters</span>
              </div>
              <input type="range" min={2} max={10} step={1} value={k} onChange={e=>setK(+e.target.value)} style={{width:'100%',accentColor:'var(--green)'}}/>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'9px',color:'var(--t3)',marginTop:'2px'}}>
                <span>2 (binary-ish)</span><span>5 (balanced)</span><span>10 (detailed)</span>
              </div>
            </div>
            <Btn onClick={runKMeans} disabled={processing}>{processing?'SEGMENTING...':'RUN K-MEANS →'}</Btn>
          </Panel>
          {segResult && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="ORIGINAL" accent="var(--t2)"><img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/></Panel>
                <Panel title={`SEGMENTED (K=${k})`} accent="var(--green)"><img src={segResult.segSrc} alt="seg" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--green)44'}}/></Panel>
              </div>
              <Panel title="CLUSTER BREAKDOWN" badge={`${k} clusters`} accent="var(--green)">
                <div style={{display:'flex',height:'28px',borderRadius:'3px',overflow:'hidden',marginBottom:'12px'}}>
                  {segResult.clusters.map((c,i)=>(
                    <div key={i} style={{flex:c.pct,background:c.color,minWidth:'2px'}} title={`${c.color} — ${c.pct}%`}/>
                  ))}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:'8px'}}>
                  {segResult.clusters.map((c,i)=>(
                    <div key={i} style={{display:'flex',alignItems:'center',gap:'8px',padding:'8px 10px',background:'var(--raised)',borderRadius:'3px',border:'1px solid var(--b0)'}}>
                      <div style={{width:'20px',height:'20px',background:c.color,borderRadius:'3px',flexShrink:0,border:'1px solid rgba(255,255,255,.15)'}}/>
                      <div>
                        <div style={{fontSize:'11px',color:'var(--t1)',fontWeight:600}}>Cluster {i+1}</div>
                        <div style={{fontSize:'10px',color:'var(--t3)'}}>{c.pct}% — {c.count.toLocaleString()}px</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {tab==='watershed' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="WATERSHED TRANSFORM — CONCEPT" accent="var(--cyan)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.8,marginBottom:'14px'}}>
              The watershed algorithm treats the grayscale image as a topographic surface — bright pixels are peaks, dark pixels are valleys. Water is "poured" from local minima (markers) and the image is gradually flooded. Where different floods meet, watershed lines are drawn — these become the segment boundaries.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px',marginBottom:'14px'}}>
              {[['1. Gradient','Compute gradient magnitude from Sobel','var(--cyan)'],['2. Markers','Define seed points (local minima)','var(--gold)'],['3. Flooding','Flood from markers upward','var(--blue)'],['4. Boundaries','Mark meeting points as watershed lines','var(--red)']].map(([step,desc,color])=>(
                <div key={step} style={{padding:'12px',background:'var(--raised)',borderLeft:`3px solid ${color}`,borderRadius:'2px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color,marginBottom:'5px'}}>{step}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
            <InfoBox color="var(--cyan)" icon="⬡">
              <strong>Best for:</strong> Separating touching objects (cells, coins, nuclei). Requires good markers — over-segmentation is common without preprocessing. Often used with distance transform for automatic marker placement.
            </InfoBox>
          </Panel>
          <Panel title="APPLICATIONS" accent="var(--cyan)">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[['Medical Imaging','Cell counting, nucleus detection in histology slides',],['Material Science','Grain boundary analysis in metallography',],['Remote Sensing','Field/region boundary detection in satellite images']].map(([title,desc])=>(
                <div key={title} style={{padding:'12px',background:'var(--raised)',borderRadius:'3px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color:'var(--cyan)',marginBottom:'5px'}}>{title}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {CONCEPTS.map(({title,color,desc})=>(
              <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!imgData && tab==='kmeans' && <InfoBox color="var(--green)" icon="⬡">Go to UPLOAD tab to load an image first.</InfoBox>}
    </div>
  );
}
